import React from 'react'
import PropTypes from 'prop-types'
import { connectCurrentRefinements } from 'react-instantsearch-dom'
import Box from '@material-ui/core/Box'
import SearchIcon from '@material-ui/icons/Search'
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'

export const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: theme.spacing(6, 2),
  },
  iconShell: {
    flexShrink: 0,
    width: 56,
    height: 56,
    marginBottom: theme.spacing(2),
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.gold.main,
    color: theme.palette.secondary.dark,
    '& > svg': {
      fontSize: 28,
    },
  },
  title: {
    fontFamily: '"Source Serif Pro", serif',
    fontSize: theme.typography.pxToRem(22),
    fontWeight: 700,
    lineHeight: 1.2,
    color: '#00234B',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.pxToRem(20),
    },
  },
  description: {
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 1.5,
    color: '#63666A',
    maxWidth: 360,
    marginBottom: theme.spacing(3),
  },
  clearButton: {
    minHeight: 40,
    padding: theme.spacing(1, 3),
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(14),
    cursor: 'pointer',
    color: '#00234B',
    border: '1px solid #00234B',
    backgroundColor: '#FAFAFA',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#F4F5F8',
      boxShadow: 'none',
    },
  },
}))

const handleClick = (items, refine, { onClearSavedOnly, onClearDirectoryFilters } = {}) => {
  onClearSavedOnly?.()
  if (onClearDirectoryFilters) {
    onClearDirectoryFilters()
    return
  }
  if (!items.length) {
    if (!onClearSavedOnly) {
      window.location.assign(`${window.location.origin}/coaches/search`)
    }
    return
  }
  refine(items)
}

export function ClearRefinements({
  items, refine, onClearSavedOnly, onClearDirectoryFilters,
}) {
  const classes = useStyles()
  return (
    <Button
      variant="outlined"
      className={classes.clearButton}
      onClick={() => handleClick(items, refine, { onClearSavedOnly, onClearDirectoryFilters })}
    >
      Clear all filters
    </Button>
  )
}

ClearRefinements.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  onClearSavedOnly: PropTypes.func,
  onClearDirectoryFilters: PropTypes.func,
}

function ConnectedClearRefinements({ onClearSavedOnly, onClearDirectoryFilters, ...props }) {
  return (
    <ClearRefinements
      {...props}
      onClearSavedOnly={onClearSavedOnly}
      onClearDirectoryFilters={onClearDirectoryFilters}
    />
  )
}

ConnectedClearRefinements.propTypes = {
  onClearSavedOnly: PropTypes.func,
  onClearDirectoryFilters: PropTypes.func,
}

const CustomClearRefinements = connectCurrentRefinements(ConnectedClearRefinements)

const getEmptyStateCopy = (savedOnlyEmpty) => {
  if (savedOnlyEmpty) {
    return {
      title: 'No saved coaches in this area',
      description: 'Save coaches you like and they will appear here.',
      showClearFilters: true,
    }
  }

  return {
    title: 'No coaches match your search',
    description: 'Try adjusting some filters.',
    showClearFilters: true,
  }
}

export function NoResultsPage({
  savedOnlyEmpty = false,
  resultsContext,
  onClearSavedOnly,
  onClearDirectoryFilters,
}) {
  const classes = useStyles()
  const { title, description, showClearFilters } = getEmptyStateCopy(savedOnlyEmpty)
  const EmptyStateIcon = savedOnlyEmpty ? FavoriteBorderIcon : SearchIcon

  return (
    <Box
      data-gtm-coach-search-no-results={resultsContext || title}
      className={classes.root}
    >
      <div className={classes.iconShell}>
        <EmptyStateIcon />
      </div>
      <Typography component="p" className={classes.title}>
        {title}
      </Typography>
      <Typography component="p" className={classes.description}>
        {description}
      </Typography>
      {showClearFilters && (
        <CustomClearRefinements
          onClearSavedOnly={onClearSavedOnly}
          onClearDirectoryFilters={onClearDirectoryFilters}
        />
      )}
    </Box>
  )
}

NoResultsPage.propTypes = {
  savedOnlyEmpty: PropTypes.bool,
  resultsContext: PropTypes.string,
  onClearSavedOnly: PropTypes.func,
  onClearDirectoryFilters: PropTypes.func,
}
